import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { individualEntryApi } from '../individualEntryApi';
import type {
  CreateIndividualEntryRequest,
  UpdateIndividualEntryRequest,
} from '../../types/individualEntry';

export const individualEntriesQueryKey = (categoryId: number) =>
  ['individual-entries', categoryId] as const;

export const useIndividualEntries = (categoryId: number) =>
  useQuery({
    queryKey: individualEntriesQueryKey(categoryId),
    queryFn: () => individualEntryApi.findByCategory(categoryId),
    enabled: !!categoryId,
  });

export const useCreateIndividualEntry = (categoryId: number) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (req: CreateIndividualEntryRequest) =>
      individualEntryApi.create(categoryId, req),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: individualEntriesQueryKey(categoryId) });
    },
  });
};

export const useUpdateIndividualEntry = (categoryId: number) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, req }: { id: number; req: UpdateIndividualEntryRequest }) =>
      individualEntryApi.update(id, req),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: individualEntriesQueryKey(categoryId) });
    },
  });
};

export const useDeleteIndividualEntry = (categoryId: number) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => individualEntryApi.remove(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: individualEntriesQueryKey(categoryId) });
    },
  });
};

export const useDrawIndividualEntryStartNumbers = (categoryId: number) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => individualEntryApi.drawStartNumbers(categoryId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: individualEntriesQueryKey(categoryId) });
    },
  });
};
